import React, { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Button } from "../Button";
import { TextField } from "../form-fields/TextField";
import { GoogleIcon } from "../../pages/get-started/LoginPage";
import { APIs } from "../../services/Apis";
import { setItem } from "../../services/Utils";
import { XMarkIcon } from "@heroicons/react/24/outline";

export const RegisterModal = ({
  isOpen,
  onClose,
  businessUrl,
  isOnboarding = false,
}) => {
  const navigate = useNavigate();
  // The business URL has no field of its own — it comes from the caller. It is
  // deliberately not mirrored into state: this modal mounts before the page has
  // read the URL out of the query string, so a copy taken at mount would stay
  // empty and fail the check below no matter what the user typed.
  const [formValues, setFormValues] = useState({
    fullName: "",
    email: "",
    password: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showVerificationStep, setShowVerificationStep] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // During onboarding, prevent API calls
    if (isOnboarding) {
      toast.info(
        "This is a preview. Complete onboarding to create your account.",
      );
      return;
    }

    const { fullName, email, password } = formValues;
    const businessurl = businessUrl || "";

    if (showVerificationStep) {
      // Verification code step
      if (!verificationCode) {
        toast.error("Please enter the verification code.");
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await APIs.register(
          fullName,
          email,
          password,
          "email",
          businessurl,
          verificationCode,
        );
        if (response.success) {
          setItem("sessionid", response.sessionid);
          setSubmitted(true);
          toast.success("Account created successfully!");
          setTimeout(() => {
            onClose();
            navigate("/dashboard");
          }, 2000);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Initial registration step
      if (!fullName || !email || !password || !businessurl) {
        toast.error("Please complete all fields.");
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await APIs.register(
          fullName,
          email,
          password,
          "email",
          businessurl,
        );
        if (response.success) {
          // Move to verification step
          setShowVerificationStep(true);
          toast.success("Verification code sent to your email!");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleGoogleSignIn = async () => {
    toast.error(
      "Google sign-in is not yet implemented with the new authentication system.",
    );
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setShowVerificationStep(false);
      setVerificationCode("");
      onClose();
    }
  };

  const handleVerificationCodeChange = (e) => {
    setVerificationCode(e.target.value);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-slate-900/70" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="relative">
            <div className="absolute -right-4 -top-4">
              <Button
                variant="outline"
                color="slate"
                size="sm"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            {submitted ? (
              <div className="flex flex-col items-center gap-4 text-center py-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
                  <CheckCircleIcon className="h-8 w-8" aria-hidden="true" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-semibold text-slate-900">
                    Account Created Successfully!
                  </h3>
                  <p className="text-sm leading-6 text-slate-600">
                    Redirecting you to your portal...
                  </p>
                </div>
              </div>
            ) : showVerificationStep ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2 text-center">
                  <h3 className="text-2xl font-semibold text-slate-900">
                    Verify your email
                  </h3>
                  <p className="text-sm text-slate-600">
                    We've sent a verification code to {formValues.email}. Please
                    enter it below.
                  </p>
                </div>

                <TextField
                  label="Verification code"
                  name="verificationCode"
                  type="text"
                  placeholder="Enter verification code"
                  required
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  disabled={isSubmitting}
                />

                <Button
                  variant="solid"
                  color="primary"
                  className="w-full justify-center py-3"
                  size="lg"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Verify and create account
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowVerificationStep(false);
                      setVerificationCode("");
                    }}
                    className="text-sm text-slate-600 hover:text-slate-900 underline"
                    disabled={isSubmitting}
                  >
                    Back to registration
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2 text-center">
                  <h3 className="text-2xl font-semibold text-slate-900">
                    Complete your Fluvo setup
                  </h3>
                  <p className="text-sm text-slate-600">
                    Create your account to activate these automations and start
                    engaging your customers.
                  </p>
                </div>

                <TextField
                  label="Your name"
                  name="fullName"
                  type="text"
                  placeholder="John Doe"
                  required
                  value={formValues.fullName}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />

                <TextField
                  label="Work email"
                  name="email"
                  type="email"
                  placeholder="youremail@yourdomain.com"
                  required
                  value={formValues.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />

                <TextField
                  label="Password"
                  name="password"
                  type="password"
                  placeholder="Create a password"
                  required
                  value={formValues.password}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />

                <Button
                  variant="solid"
                  color="primary"
                  className="w-full justify-center py-3"
                  size="lg"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Finish setup
                </Button>

                {/* <div className="mt-2 flex items-center">
                  <div className="h-px flex-1 bg-gray-200"></div>
                  <span className="px-4 text-sm text-gray-500">
                    or continue with
                  </span>
                  <div className="h-px flex-1 bg-gray-200"></div>
                </div>

                <div className="grid grid-cols-1">
                  <Button
                    variant="solid"
                    color="primaryLight"
                    className="w-full py-3 flex justify-center items-center gap-2"
                    size="lg"
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isSubmitting}
                  >
                    <GoogleIcon />
                    <span>Sign up with Google</span>
                  </Button>
                </div> */}
              </form>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
